/* eslint-disable no-dupe-class-members */
import type { PollStorage, PollStoredState } from 'components-machine';
import { PollsLocalStorage } from './pollsLocalStorage';
import { PollsSiteStorage } from './pollsSiteStorage';

const POLL_ENV: 'prod' | 'dev' = window?.__pollData?.env ?? 'dev';
const ROOT_KEY = POLL_ENV === 'prod' ? '@tinkoff/polls' : '@tinkoff/polls-test';

const createPollsLocalStorage = (pollId: string) => {
  const storageKey = `${ROOT_KEY}/${pollId}`;
  return new PollsLocalStorage(storageKey);
};

const delay = (ms: number) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

type ActiveStorage = PollStorage & {
  update: (data: Partial<PollStoredState>, { force }: { force: boolean }) => Promise<void>;
};

/** Объект для работы с хранилищем (Polls site или local storage). Использует два хранилища: основное и резервное.
 *  В случае ошибок основного хранилища переключается на резервное. По умолчанию используется local storage.
 *  Поддерживает повторные запросы к хранилищу - в случае успеха переключается на основное хранилище. */
export class PollStoragePool implements PollStorage {
  private retryCount: number;
  private maxRetryCount: number;
  private retryTimeoutMs: number;

  private latestState: Partial<PollStoredState> | null;
  private activeStorage: ActiveStorage;
  private isPrimaryAvailable: boolean;

  private pollId: string;

  /**
   * Создание хранилища
   * @param {object} config - конфигурация
   * @param {string} config.pollId - id опроса
   * @param {number} [config.retryTimeoutMs=5000] - интервал между повторными запросами в миллисекундах
   * @param {number} [config.retryCount=1] - количество повторных запросов в случае неудачи
   */
  constructor({
    pollId,
    retryCount = 1,
    retryTimeoutMs = 5000,
  }: {
    pollId: string;
    retryCount?: number;
    retryTimeoutMs?: number;
  }) {
    this.retryCount = 0;
    this.maxRetryCount = retryCount;
    this.retryTimeoutMs = retryTimeoutMs;

    this.pollId = pollId;

    this.latestState = null;
    this.activeStorage = this.createActiveStorage({ primaryStorage: createPollsLocalStorage(pollId) });
    this.isPrimaryAvailable = true;
  }

  /**
   * Создает композитное хранилище из двух
   * @param {object} storages - конфигурация хранилищ
   * @param {object} storages.primaryStorage - основное (приоритетное) хранилище. Используется по умолчанию, если доступно
   * @param {object} [storages.fallbackStorage] - резервное хранилище. Используется в случае, если основное хранилище недоступно
   */
  private createActiveStorage({
    primaryStorage,
    fallbackStorage = null,
  }: {
    primaryStorage: PollStorage;
    fallbackStorage?: PollStorage | null;
  }) {
    const handler: ProxyHandler<ActiveStorage> = {
      get: (target, prop) => {
        const storageProp = prop as keyof ActiveStorage;

        const primaryMethod = target[storageProp];
        const fallbackMethod = fallbackStorage?.[storageProp];

        if (typeof primaryMethod !== 'function') {
          return primaryMethod;
        }

        return async (...args: Parameters<typeof primaryMethod>) => {
          const getOperationArgs = () => {
            return storageProp === 'update' && this.latestState ? [this.latestState] : args;
          };

          const [, options] = args;
          const { force } = options ?? { force: false };

          const canRetry = this.retryCount < this.maxRetryCount || force;

          const primaryOperation = () =>
            (primaryMethod as (...args: unknown[]) => Promise<unknown>).apply(primaryStorage, getOperationArgs());
          const fallbackOperation = () => {
            if (fallbackMethod) {
              return (fallbackMethod as (...args: unknown[]) => unknown).apply(fallbackStorage, getOperationArgs());
            }

            console.error('Fallback is undefined');
          };

          if (!this.isPrimaryAvailable && !canRetry) {
            return fallbackOperation();
          }

          try {
            const result = await primaryOperation();
            await fallbackStorage?.remove();
            return result;
          } catch {
            this.isPrimaryAvailable = false;
            if (canRetry) void this.initRetry(primaryOperation);
            return fallbackOperation();
          }
        };
      },
    };

    return new Proxy(primaryStorage, handler);
  }

  /**
   * Инициирует повторные запросы к основному хранилищу, используя переданный метод
   * @param {object} operation - метод хранилища
   */
  private async initRetry(operation: () => Promise<unknown>) {
    let successfullyRetried = false;

    while (this.retryCount < this.maxRetryCount && !successfullyRetried) {
      this.retryCount += 1;

      try {
        await delay(this.retryTimeoutMs);
        await operation();

        this.retryCount = 0;
        this.isPrimaryAvailable = true;
        successfullyRetried = true;
      } catch {
        /* empty */
      }
    }
  }

  /**
   * Получение состояния прохождения из хранилища
   * @param {string} [key] - свойство состояния, значение которого будет возвращено
   */
  public get(): Promise<Partial<PollStoredState> | null>;
  public get<TKey extends keyof PollStoredState>(key: TKey): Promise<PollStoredState[TKey] | null>;
  public async get<TKey extends keyof PollStoredState>(key?: TKey) {
    return key ? this.activeStorage.get(key) : this.activeStorage.get();
  }

  /**
   * Обновляет состояние прохождения в хранилище
   * @param {object} data - состояние прохождение опроса, возможно использование неполного состояния - остальные будут взяты из предыдущего состояния
   * @param {object} [options] - флаги
   * @param {boolean} [options.force] - попытаться использовать основное хранилище, даже если ранее оно было недоступно
   */
  public async update(data: Partial<PollStoredState>, { force }: { force: boolean } = { force: false }): Promise<void> {
    this.latestState = data;
    await this.activeStorage.update(data, { force });
  }

  /**
   * Удаляет состояние прохождения из хранилища
   */
  public remove(): Promise<void> {
    return this.activeStorage.remove();
  }

  /**
   * Проверяет возможность иницировать переход на хранилище polls site без tracking_id
   * MACP-1647: Со стороны компонентов должен быть отправлен referer, а не tracking_id
   */
  public canEnablePollsSiteWithoutTrackingId() {
    const referer = new URL(window.location.href);

    // В url-строке опроса содержатся параметры _pcid или _peqp
    return ['_pcid', '_peqp'].some(key => referer.searchParams.has(key));
  }

  /**
   * Инициирует переход на хранилище Polls site. В случае ошибок используется local storage
   * @param {object} config - конфигурация хранилища Polls site
   * @param {string} config.trackingId - уникальный идентификатор контекста прохождения Polls site. Может быть использована любая строка - нет заданного формата
   * @param {string} config.expireTimeMs - время жизни контекста прохождения Polls site в миллисекундах
   */
  public async enablePollsSiteStorage({
    trackingId,
    expireTimeMs,
  }: {
    trackingId: string | null;
    expireTimeMs: number | null;
  }) {
    const previousState = await this.activeStorage.get();

    const pollsSiteStorage = new PollsSiteStorage({ pollId: this.pollId, trackingId, expireTimeMs });
    const pollsLocalStorage = createPollsLocalStorage(this.pollId);

    this.activeStorage = this.createActiveStorage({
      primaryStorage: pollsSiteStorage,
      fallbackStorage: pollsLocalStorage,
    });
    const currentState = await this.activeStorage.get();

    if (!currentState && previousState) {
      await this.activeStorage.update(previousState);
    }
  }
}
